import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { getAccessToken } from "../axios-orders";
import {
  ActivityLog,
  OfferResponse,
  ORDER_TYPES,
  OrderResponse,
  OrderState,
  SimpleOrderPaginatedResponse,
  UserResponse,
  AwardedOrderResponse,
  AllOrdersResponse,
  SupplierRfqActivity,
} from "../types";
import { queryKeys } from "./queryKeys";

const onGetJointOrders = async (state: OrderState) => {
  const { data } = await axios.get(`/order/multiple/state/${state}`);
  return data;
};

export const useJointOrders = (state: OrderState) =>
  useQuery({
    queryKey: [queryKeys.JOINT_ORDERS, state],
    queryFn: () => onGetJointOrders(state),
    select: (data) => {
      const items = Object.keys(data).map((key) => data[key]);
      return items as [OrderResponse[]];
    },
    retry: false,
  });

const onGetOrders = async (
  state: OrderState,
  type: ORDER_TYPES,
  _type: ORDER_TYPES,
  page = 0
): Promise<SimpleOrderPaginatedResponse> => {
  const { data } = await axios.get(`/order/${state}/${type}/${_type}/${page}`);
  return data;
};

export const useOrdersByTypeAndState = (
  state: OrderState,
  type: ORDER_TYPES,
  _type: ORDER_TYPES,
  page = 0
) =>
  useQuery<SimpleOrderPaginatedResponse>({
    queryKey: [queryKeys.ORDERS, page],
    queryFn: () => onGetOrders(state, type, _type, page),
    retry: false,
  });

const onGetJointOrder = async (orderId: string) => {
  const { data } = await axios.get(`/order/multiple/${orderId}`);
  return data;
};

export const useJointOrder = (orderId: string, enabled?: boolean) =>
  useQuery<OrderResponse[]>({
    queryKey: [queryKeys.JOINT_ORDER, orderId],
    queryFn: () => onGetJointOrder(orderId),
    enabled: !!orderId && enabled,
    retry: false,
  });

const onGetOrder = async (orderId: string) => {
  const { data } = await axios.get(`/order/${orderId}`);
  return data;
};

export const useOrder = (orderId: string, enabled: boolean = true) =>
  useQuery<OrderResponse>({
    queryKey: [queryKeys.ORDER, orderId],
    queryFn: () => onGetOrder(orderId),
    enabled: enabled && !!orderId,
    retry: false,
  });

// Shared blob-download helper. Used by the report download mutations so each
// caller gets an isPending flag they can use to disable the trigger button.
const downloadFile = async (path: string, filename: string): Promise<void> => {
  const response = await fetch(process.env.REACT_APP_API + path, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + getAccessToken(),
    },
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const useDownloadBidReport = () =>
  useMutation({
    mutationFn: (orderId: string) =>
      downloadFile(
        `/utils/bid-report/${orderId}/pdf`,
        `reporte-licitacion-${orderId}.pdf`
      ),
  });

export const useDownloadJointBidReport = () =>
  useMutation({
    mutationFn: (multipleOrderId: string) =>
      downloadFile(
        `/utils/bid-report/multiple/${multipleOrderId}/pdf`,
        `reporte-licitacion-${multipleOrderId}.pdf`
      ),
  });

export const useDownloadOrderComparison = () =>
  useMutation({
    mutationFn: (orderId: string) =>
      downloadFile(
        `/utils/comparison-report/${orderId}`,
        `comparison-report-${orderId}.xls`
      ),
  });

const onGetSelectedOrder = async (orderId: string) => {
  const { data } = await axios.get(`/offer/order/selected/${orderId}`);
  return data;
};

export const useSelectedOrder = (orderId: string) =>
  useQuery<OfferResponse[]>({
    queryKey: [queryKeys.TENDER, orderId],
    queryFn: () => onGetSelectedOrder(orderId),
  });

const onCreateOrder = async (body: any) => {
  const { data } = await axios.post("/order", body);
  return data;
};

export const useCreateOrder = (
  onSuccess: (res: any) => void,
  onError: (err: unknown) => void
) =>
  useMutation({
    mutationFn: onCreateOrder,
    onSuccess,
    onError,
  });

const onCreateMultipleOrder = async (body: any) => {
  const { data } = await axios.post("/order/multiple", body);
  return data;
};

export const useCreateMultipleOrder = (
  onSuccess: (res: any) => void,
  onError: (err: any) => void
) =>
  useMutation({
    mutationFn: onCreateMultipleOrder,
    onSuccess,
    onError,
  });

const onDeleteOrder = async ({
  orderId,
  order,
}: {
  orderId: string;
  order: any;
}) => {
  const { data } = await axios.put(`/order/${orderId}`, order);
  return data;
};

// Cancelling a quotation releases its linked Solped item back to a re-quotable
// state on the backend. Refresh the Solped list/detail caches (both requester
// and buyer views) so they reflect the release instead of the stale pre-cancel
// state. Used by both the single and joint cancel hooks.
const invalidatePurchaseRequestV2Caches = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  [
    queryKeys.PURCHASE_REQUESTS_V2_ME_REQUESTER,
    queryKeys.PURCHASE_REQUESTS_V2_ME,
    queryKeys.PURCHASE_REQUESTS_V2_COMPANY,
    queryKeys.PURCHASE_REQUEST_V2,
    queryKeys.PURCHASE_REQUEST_V2_ITEMS,
    queryKeys.PURCHASE_REQUEST_V2_BUYER_ITEMS,
    queryKeys.PURCHASE_REQUEST_V2_COMPANY_ITEMS,
  ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
};

export const useDeleteOrder = (onSuccess: () => void, onError: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onDeleteOrder,
    onSuccess: () => {
      invalidatePurchaseRequestV2Caches(queryClient);
      onSuccess();
    },
    onError,
  });
};

// Edit-mode submit for OrderByRequest. The launch form derives the `id` of
// the entry from `mat.orderId` and routes here when present so the existing
// order is updated in place rather than a new one created.
const onUpdateOrder = async (body: any) => {
  const { data } = await axios.put(`/order/${body.id}`, body);
  return data;
};

export const useUpdateOrder = (
  onSuccess: (res: any) => void,
  onError: (err: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUpdateOrder,
    onSuccess: (res, variables) => {
      // Invalidate the single-order cache so any open detail view picks up
      // the persisted change, plus the list keys consumed by /orders so the
      // user sees fresh data after navigating back.
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ORDER, variables?.id],
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ORDERS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ALL_ORDERS] });
      onSuccess(res);
    },
    onError,
  });
};

// Post-quotation adjudication (General #1).
export type FinalizedOrderSummary = {
  orderId: string;
  materialName: string | null;
  internalCode: string | null;
  quantity: number | null;
  measurementUnit: string | null;
  awardedSupplierName: string | null;
};

export type FinalizeAdjudicationResponse = {
  awarded: FinalizedOrderSummary[];
  orphan: FinalizedOrderSummary[];
};

const onFinalizeAdjudication = async (
  orderId: string
): Promise<FinalizeAdjudicationResponse> => {
  const { data } = await axios.post(`/order/${orderId}/finalize-adjudication`);
  return data;
};

export const useFinalizeAdjudication = (
  onSuccess: (data: FinalizeAdjudicationResponse) => void,
  onError: () => void
) =>
  useMutation({
    mutationFn: onFinalizeAdjudication,
    onSuccess,
    onError,
  });

export type SecondRoundEligibleSupplier = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  companyName: string | null;
  submittedOffer: boolean;
  totalOfferAmount: number | null;
  currency: string | null;
};

const onGetSecondRoundEligibleSuppliers = async (
  orderId: string
): Promise<SecondRoundEligibleSupplier[]> => {
  const { data } = await axios.get(
    `/order/${orderId}/second-round/eligible-suppliers`
  );
  return data;
};

export const useSecondRoundEligibleSuppliers = (
  orderId: string,
  enabled: boolean
) =>
  useQuery<SecondRoundEligibleSupplier[]>({
    queryKey: [queryKeys.SECOND_ROUND_ELIGIBLE_SUPPLIERS, orderId],
    queryFn: () => onGetSecondRoundEligibleSuppliers(orderId),
    enabled: enabled && !!orderId,
  });

const onLaunchSecondRound = async ({
  orderId,
  startDate,
  finishDate,
  opensAt,
  closesAt,
  supplierIds,
}: {
  orderId: string;
  startDate: string;
  finishDate: string;
  opensAt?: string;
  closesAt?: string;
  supplierIds?: string[];
}) => {
  const { data } = await axios.post(`/order/${orderId}/second-round`, {
    startDate,
    finishDate,
    opensAt,
    closesAt,
    supplierIds,
  });
  return data;
};

export const useLaunchSecondRound = (
  onSuccess: () => void,
  onError: () => void
) =>
  useMutation({
    mutationFn: onLaunchSecondRound,
    onSuccess,
    onError,
  });

const onDeleteMultipleOrders = async (orders: any) => {
  const { data } = await axios.put(`/order/list`, orders);
  return data;
};

export const useDeleteMultipleOrders = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onDeleteMultipleOrders,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_ORDER_CHAT],
      });
      invalidatePurchaseRequestV2Caches(queryClient);
      onSuccess();
    },
    onError,
  });
};

const onGetOrdersCount = async () => {
  const { data } = await axios.get("/order/count");
  return data;
};

export const useOrdersCount = () =>
  useQuery({
    queryKey: [queryKeys.ORDERS_COUNT],
    queryFn: () => onGetOrdersCount(),
  });

const onGetAwardedOrders = async () => {
  const { data } = await axios.get(`/order/succeed-without-deliveries`);
  return data;
};

export const useAwardedOrders = () =>
  useQuery<AwardedOrderResponse[], Error>({
    queryKey: [queryKeys.AWARDED_ORDERS],
    queryFn: () => onGetAwardedOrders(),
  });

const onGetAwardedSuppliersByOrderId = async (orderId: string) => {
  const { data } = await axios.get(`/order/${orderId}/awarded-suppliers`);
  return data;
};

export const useAwardedSuppliersByOrderId = (orderId: string) =>
  useQuery<UserResponse[], Error>({
    queryKey: [queryKeys.AWARDED_SUPPLIERS_BY_ORDER_ID, orderId],
    queryFn: () => onGetAwardedSuppliersByOrderId(orderId),
    enabled: !!orderId,
  });

export interface AllOrdersParams {
  page?: number;
  size?: number;
  search?: string;
  states?: string[];
  types?: string[];
  startDateFrom?: string;
  startDateTo?: string;
  currency?: string;
  urgent?: boolean;
  /** Owner filter for the unified (company) orders view: restrict to these users. Empty/absent =
   * every user in the company. Ignored by the user-scoped endpoint. */
  userIds?: string[];
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
}

const onGetAllOrders = async (url: string, params: AllOrdersParams = {}) => {
  const {
    page = 0,
    size,
    search,
    states,
    types,
    startDateFrom,
    startDateTo,
    currency,
    userIds,
    sortBy,
    sortDirection,
  } = params;

  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());

  if (typeof size === "number" && size > 0) {
    queryParams.append("size", size.toString());
  }

  if (search) {
    queryParams.append("search", search);
  }

  if (states && states.length > 0) {
    queryParams.append("states", states.join(","));
  }

  if (types && types.length > 0) {
    queryParams.append("types", types.join(","));
  }

  if (startDateFrom) {
    queryParams.append("startDateFrom", startDateFrom);
  }

  if (startDateTo) {
    queryParams.append("startDateTo", startDateTo);
  }

  if (currency) {
    queryParams.append("currency", currency);
  }

  if (userIds && userIds.length > 0) {
    userIds.forEach((id) => queryParams.append("userIds", id));
  }

  if (sortBy) {
    queryParams.append("sortBy", sortBy);
  }

  if (sortDirection) {
    queryParams.append("sortDirection", sortDirection);
  }

  const { data } = await axios.get(url, {
    params: queryParams,
  });
  return data;
};

export const useAllOrders = (params: AllOrdersParams = {}) =>
  useQuery<AllOrdersResponse, Error>({
    queryKey: [queryKeys.ALL_ORDERS, params],
    queryFn: () => onGetAllOrders(`/order/company/orders`, params),
    retry: false,
  });

export const useOrders = (params: AllOrdersParams = {}) =>
  useQuery<AllOrdersResponse, Error>({
    queryKey: [queryKeys.ALL_ORDERS, params],
    queryFn: () => onGetAllOrders(`/order/user/orders`, params),
    retry: false,
  });

// The supplier home's unified RFQ list — the open orders a seller was invited to
// and can still quote on, simple and joint in one paginated envelope. Returns the
// same `AllOrdersResponse` shape as the buyer endpoints, so the shared
// `orderListRows` flattener/rows render it unchanged.
export const useSupplierRfqs = (params: AllOrdersParams = {}) =>
  useQuery<AllOrdersResponse, Error>({
    queryKey: [queryKeys.SUPPLIER_RFQS, params],
    queryFn: () => onGetAllOrders(`/order/suppliers/rfqs`, params),
    retry: false,
  });

// The seller's full "biddings" list (`/biddings`) — every RFQ they were invited to across ALL
// states (optionally filtered), including already-quoted ones. Same `AllOrdersResponse` shape as the
// RFQ list, plus per-item `hasOffered`/`offerId` for row routing.
export const useSupplierBiddings = (params: AllOrdersParams = {}) =>
  useQuery<AllOrdersResponse, Error>({
    queryKey: [queryKeys.SUPPLIER_BIDDINGS, params],
    queryFn: () => onGetAllOrders(`/order/suppliers/biddings`, params),
    retry: false,
  });

const onGetOrderActivityLog = async (id: string): Promise<ActivityLog[]> => {
  const { data } = await axios.get(`/order/${id}/activity-log`);
  return data;
};

export const useOrderActivityLog = (id: string | undefined) =>
  useQuery({
    queryKey: [queryKeys.ORDER_ACTIVITY_LOG, id],
    queryFn: () => onGetOrderActivityLog(id!),
    enabled: !!id,
  });

const onGetOrderSupplierActivity = async (
  id: string
): Promise<SupplierRfqActivity[]> => {
  const { data } = await axios.get(`/order/${id}/supplier-activity`);
  return data;
};

// What each invited supplier did with the buyer's RFQ (invited, opened the email, opened the RFQ,
// quoted). For a joint RFQ any line's id returns the whole group.
export const useOrderSupplierActivity = (id: string | undefined) =>
  useQuery({
    queryKey: [queryKeys.ORDER_SUPPLIER_ACTIVITY, id],
    queryFn: () => onGetOrderSupplierActivity(id!),
    enabled: !!id,
  });
